import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import type { KBAQuestionType } from '@ps/web-core-store';
import { useCallback } from 'react';
import { useStore } from 'react-redux';

import { Country } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { Store } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';
import type { RealMoneyInformation } from '@swf/first';
import { CashierFlowType } from '@swf/first';

import type { FeatureFlagData } from '../../Signup/hooks/useSignupGuards';

export enum Guard {
  CheckRMSuspended = 'checkRMSuspended',
  CheckUserFlags = 'checkUserFlags',
  DoesJumpToFeatureStep = 'doesJumpToFeatureStep',
  PhoneVerificationCheck = 'phoneVerificationCheck',
  RmfChallengeQuestionsCheck = 'rmfChallengeQuestionsCheck',
  RmfDobEnabled = 'rmfDobEnabled',
  ShowPendingVerification = 'showPendingVerification',
  SignupDepositEnabled = 'signupDepositEnabled',
  PhoneCollectionCheck = 'phoneCollectionCheck',
  GoToDepositLimits = 'goToDepositLimits',
  LimitsSelected = 'limitsSelected',
  SingleSignUpEnabled = 'singleSignUpEnabled',
  ShowSumSubFirst = 'sumSubFirst',
  CheckSumSubVerification = 'checkSumSubVerification',
  isCashierPath = 'isCashierPath',
  CheckRetryUserFlags = 'checkRetryUserFlags',
  RecheckUserFlags = 'recheckUserFlags',
  IsRetryScreenEnabled = 'isRetryScreenEnabled',
  UserStatusInSpain = 'userStatusInSpain',
  CheckUserAccountStatus = 'checkUserAccountStatus',
  checkPoker = 'checkPoker',
  checkCasino = 'checkCasino',
  showSGScreen = 'showSGScreen',
  CheckSGCScreenAndSelection = 'checkSGCScreenAndSelection',
  StandaloneRmf = 'standaloneRmf',
  WrmfKBA = 'wrmfKBA'
}

const useRMFGuards = () => {
  const store = useStore() as Store;
  const isSumSubEnabled = useFeature(FeatureFlag.SwcSignupRmfSumSub);
  const isRetryScreenEnabled = useFeature(FeatureFlag.SwcRetryScreen);
  const showKbaScreen = useFeature(FeatureFlag.WrmfKBA);
  const showSGScreen = useFeature(FeatureFlag.SwcSGScreen);

  const {
    data: { cashierFlow }
  } = useWizardContext();

  const doesJumpToFeatureStep = useCallback(
    ({
      data: { cashierFlow, rmfTargetToJump }
    }: {
      data: { cashierFlow: CashierFlowType; rmfTargetToJump?: string };
    }): boolean =>
      [CashierFlowType.FirstWithdraw, CashierFlowType.FirstP2pTransfer].includes(cashierFlow) ||
      !!rmfTargetToJump,
    []
  );

  const rmfDobEnabled = useCallback(
    ({
      data: {
        userInfo: { dateOfBirth }
      }
    }: {
      data: { userInfo: { dateOfBirth: string } };
    }) => !dateOfBirth,
    []
  );

  const rmfChallengeQuestionsCheck = useCallback(
    ({
      data: { realMoneyInformation }
    }: {
      data: { realMoneyInformation: RealMoneyInformation | null };
    }) => !!realMoneyInformation?.securityQuestions,
    []
  );

  const signupDepositEnabled = () =>
    select.feature.check(store.getState(), FeatureFlag.SwcSignupDeposit);

  const checkUserFlags = useCallback(() => {
    const rootState = store.getState();
    const isKYCRequired = store.select.account.isKYCRequired(rootState);
    const isAuthVerified = store.select.account.isAuthVerified(rootState);
    const isAgeVerified = store.select.account.isAgeVerified(rootState);
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);

    return (
      isKYCRequired || (!isKYCRequired && (!isAuthVerified || !isAgeVerified || !isRealMoneyOK))
    );
  }, [store]);

  const checkRetryUserFlags = useCallback(() => {
    const rootState = store.getState();
    const isAuthVerified = store.select.account.isAuthVerified(rootState);
    const isAgeVerified = store.select.account.isAgeVerified(rootState);
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);

    return !isAuthVerified || !isAgeVerified || !isRealMoneyOK;
  }, [store]);

  const checkUserAccountStatus = useCallback(() => {
    const rootState = store.getState();
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);
    const isAuthVerified = store.select.account.isAuthVerified(rootState);

    return isRealMoneyOK && isAuthVerified;
  }, [store]);

  const showPendingVerification = useCallback(
    () => !store.select.account.isKYCRequired(store.getState()),
    [store]
  );

  const checkRMSuspended = useCallback(
    () => store.select.account.isSuspendRM(store.getState()),
    [store]
  );

  const phoneVerificationCheck = useCallback(
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcRMFPhoneVerification],
    []
  );

  const phoneCollectionCheck = useCallback(
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcSignupPhoneCollection],
    []
  );

  const singleSignUpEnabled = useCallback(
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcSignupOnePage],
    []
  );

  const isCashierPath = useCallback(
    ({ data: { cashierFlow } }: { data: { cashierFlow: CashierFlowType } }) =>
      [CashierFlowType.FirstDeposit, CashierFlowType.FirstWithdraw].includes(cashierFlow),
    []
  );

  const showLimitsScreen = useCallback(
    ({ values: { userLimitsSelect } }: Pick<WizardContext, 'values'>) => userLimitsSelect?.skipped,
    []
  );

  const checkSGCScreenAndSelection = useCallback(
    ({ values: { userLimitsSelect } }: Pick<WizardContext, 'values'>) =>
      userLimitsSelect.skipped && showSGScreen,
    [showSGScreen]
  );

  const showSumSubFirst = useCallback(
    ({ values: { country } }: Pick<WizardContext, 'values'>) =>
      country === Country.Brazil && isSumSubEnabled,
    [isSumSubEnabled]
  );

  const checkSumSubVerification = useCallback(
    ({ values: { sumSubVerification } }: Pick<WizardContext, 'values'>) =>
      sumSubVerification.pased && (!CashierFlowType.FirstWithdraw.includes(cashierFlow) as boolean),
    [cashierFlow]
  );

  const retryScreenEnabled = useCallback(() => isRetryScreenEnabled, [isRetryScreenEnabled]);

  const kbaScreenEnabled = useCallback(
    ({ data: { kbaQuestions } }: { data: { kbaQuestions: KBAQuestionType[] } }) =>
      showKbaScreen && !!kbaQuestions?.length,
    [showKbaScreen]
  );

  const checkCasino = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return registrationBrand === BrandTypes.STARS_CASINO;
  }, [store]);

  const checkPoker = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return !registrationBrand || registrationBrand === BrandTypes.POKER_STARS;
  }, [store]);

  const shouldShowSGScreen = useCallback(() => showSGScreen, [showSGScreen]);

  const userStatusInSpain = useCallback(
    ({ values: { country, citizenship } }: Pick<WizardContext, 'values'>) =>
      country === Country.Spain || citizenship === Country.Spain,
    []
  );

  const standaloneRMF = useCallback(() => {
    const rootState = store.getState();
    const pathName = store.select.location.getPathname(rootState);
    return pathName.includes('real-money-form');
  }, [store]);

  return {
    [Guard.CheckRMSuspended]: checkRMSuspended,
    [Guard.CheckUserFlags]: checkUserFlags,
    [Guard.DoesJumpToFeatureStep]: doesJumpToFeatureStep,
    [Guard.PhoneVerificationCheck]: phoneVerificationCheck,
    [Guard.RmfChallengeQuestionsCheck]: rmfChallengeQuestionsCheck,
    [Guard.RmfDobEnabled]: rmfDobEnabled,
    [Guard.ShowPendingVerification]: showPendingVerification,
    [Guard.SignupDepositEnabled]: signupDepositEnabled,
    [Guard.PhoneCollectionCheck]: phoneCollectionCheck,
    [Guard.LimitsSelected]: showLimitsScreen,
    [Guard.SingleSignUpEnabled]: singleSignUpEnabled,
    [Guard.ShowSumSubFirst]: showSumSubFirst,
    [Guard.CheckSumSubVerification]: checkSumSubVerification,
    [Guard.isCashierPath]: isCashierPath,
    [Guard.CheckRetryUserFlags]: checkRetryUserFlags,
    [Guard.RecheckUserFlags]: checkUserFlags,
    [Guard.IsRetryScreenEnabled]: retryScreenEnabled,
    [Guard.UserStatusInSpain]: userStatusInSpain,
    [Guard.CheckUserAccountStatus]: checkUserAccountStatus,
    [Guard.checkPoker]: checkPoker,
    [Guard.checkCasino]: checkCasino,
    [Guard.showSGScreen]: shouldShowSGScreen,
    [Guard.CheckSGCScreenAndSelection]: checkSGCScreenAndSelection,
    [Guard.StandaloneRmf]: standaloneRMF,
    [Guard.WrmfKBA]: kbaScreenEnabled
  };
};

export default useRMFGuards;
